import {
  Table,
  Checkbox,
  CheckboxProps,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  Typography,
  Button,
} from '@mui/material'
import { getRuleSetting, FieldRule, saveRuleSetting, OperationRule } from 'api/rule'
import FormInput from 'components/ui/FormInput'
import { DOC_TYPE } from 'entities/Documents'
import { LABELS as INVOICE_LABEL } from 'entities/DocumentsInvoiceBase'
import { LABELS as ORDER_FORM_LABEL } from 'entities/DocumentsOrderForm'
import { useAlert } from 'hooks/useDialog'
import { useEffect, useState, useCallback } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import { getReportType } from 'utils/setting'

import NotFoundMessage from './NotFoundMessage'

const reportSpecificField = (name: string) => {
  return name.split('.')
}

const tableCategory = {
  headerBase: '一般情報',
  headerBillFrom: '請求元企業情報',
  headerBillTo: '請求先情報',
  headerBank: '振込先情報',
  headerContact: '問合せ先情報',
  headerOrderer: '発行元情報',
  headerSupplier: '注文先情報',
  headerDelivery: '納入先情報',
  itemLines: '品目情報',
  other: 'その他',
}

const CheckAllBox = (props: CheckboxProps) => (
  <Checkbox size="small" color="primary" {...props} style={{ paddingLeft: 0 }} />
)

export default function RuleEdit() {
  const location = useLocation()
  const navigate = useNavigate()

  const { rule } = location.state || {}
  const alert = useAlert()

  const [loading, setLoading] = useState(true)
  const [ruleSetting, setRuleSetting] = useState<OperationRule | null>(null)

  const form = useForm<{ mappings: FieldRule[] }>()
  const { getValues, setValue, watch } = form

  useEffect(() => {
    if (!rule) return
    setLoading(true)
    getRuleSetting(rule.subGroup, rule.documentType, rule.ocrBuilderConfigID)
      .then((data) => {
        setRuleSetting(data)
      })
      .finally(() => setLoading(false))
  }, [rule])

  const mappings = watch().mappings || ruleSetting?.rules

  const allChecked = (() => {
    const search = {
      display: { tFound: false, fFound: false },
      edit: { tFound: false, fFound: false },
      verify: { tFound: false, fFound: false },
    }
    mappings?.forEach((m) => {
      for (const k in search) {
        m[k] ? (search[k].tFound = true) : (search[k].fFound = true)
      }
    })

    return {
      display: {
        allchecked: search.display.tFound && !search.display.fFound,
        indeterminate: search.display.tFound && search.display.fFound,
      },
      edit: {
        allchecked: search.edit.tFound && !search.edit.fFound,
        indeterminate: search.edit.tFound && search.edit.fFound,
      },
      verify: {
        allchecked: search.verify.tFound && !search.verify.fFound,
        indeterminate: search.verify.tFound && search.verify.fFound,
      },
    }
  })()

  const goBack = useCallback(() => navigate('..'), [navigate])

  const onSubmit = async ({ mappings }) => {
    if (mappings.every((m) => m.display === false)) {
      return await alert({ message: '最低一つは表示設定する必要があります' })
    }
    try {
      await saveRuleSetting({
        subGroup: rule.subGroup,
        ocrBuilderConfigID: rule.ocrBuilderConfigID,
        documentType: rule.documentType,
        documentTypeName: rule.documentTypeName,
        mappings: mappings,
      })
      alert({ message: '運用ルールの設定が保存されました' })
      goBack()
    } catch {
      alert({ message: '運用ルールの設定の保存が失敗しました' })
    }
  }

  const isReportSpecific =
    rule.documentType === DOC_TYPE.INVOICE || rule.documentType === DOC_TYPE.ORDER_FORM

  const handleCheckAll = useCallback(
    (checkType: 'display' | 'edit' | 'verify', checked: boolean) => {
      getValues().mappings.forEach((m, idx) =>
        setValue(`mappings.${idx}.${checkType}` as const, checked),
      )
    },
    [getValues, setValue],
  )

  if (!rule) return <NotFoundMessage isNewTab />

  if (loading) {
    return <div> </div>
  }

  const getLabel = (category, fieldName) => {
    if (rule.documentType === DOC_TYPE.INVOICE) {
      return INVOICE_LABEL[category][fieldName]
    } else if (rule.documentType === DOC_TYPE.ORDER_FORM) {
      return ORDER_FORM_LABEL[category][fieldName]
    }
    return fieldName
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Box display="flex" justifyContent="space-between" mb={3}>
          <Box>
            <Typography variant="h6" component="span">
              {getReportType(rule.documentType)}: {rule.documentTypeName}
            </Typography>{' '}
            {rule.subGroup}
          </Box>
          <Box>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate(-1)}
              data-testid="cancel-button"
            >
              キャンセル
            </Button>
            <Button type="submit" variant="contained" color="primary" data-testid="save-button">
              保存
            </Button>
          </Box>
        </Box>
        <Table size="small" aria-label="a dense table">
          <TableHead data-testid="table-head-operation-rule-edit-settings">
            <TableRow>
              {isReportSpecific && <TableCell>カテゴリ</TableCell>}
              <TableCell>フィールド名</TableCell>
              <TableCell>
                <CheckAllBox
                  onChange={(e) => {
                    handleCheckAll('display', e.target.checked)
                  }}
                  checked={allChecked.display.allchecked}
                  indeterminate={allChecked.display.indeterminate}
                />
                表示{' '}
              </TableCell>
              <TableCell>
                <CheckAllBox
                  onChange={(e) => {
                    handleCheckAll('edit', e.target.checked)
                  }}
                  checked={allChecked.edit.allchecked}
                  indeterminate={allChecked.edit.indeterminate}
                />
                編集
              </TableCell>
              <TableCell>
                <CheckAllBox
                  onChange={(e) => {
                    handleCheckAll('verify', e.target.checked)
                  }}
                  checked={allChecked.verify.allchecked}
                  indeterminate={allChecked.verify.indeterminate}
                />
                ベリファイ
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody data-testid="table-body-operation-rule-edit-settings">
            {ruleSetting?.rules!.map((row, idx) => {
              const [category, fieldName] = isReportSpecific
                ? reportSpecificField(row.fieldName)
                : [undefined, row.fieldName]
              const displayFieldName = getLabel(category, fieldName)
              if (!displayFieldName) {
                return
              }
              return (
                <TableRow key={`${row.no}_${row.fieldName}`}>
                  {isReportSpecific && (
                    <TableCell component="th" scope="row">
                      {category ? tableCategory[category] : category}
                    </TableCell>
                  )}
                  <TableCell component="th" scope="row">
                    {displayFieldName}
                  </TableCell>
                  <TableCell>
                    <FormInput name={`mappings.${idx}.no`} type="hidden" defaultValue={row.no} />
                    <FormInput
                      name={`mappings.${idx}.order`}
                      type="hidden"
                      defaultValue={row.order}
                    />
                    <FormInput
                      name={`mappings.${idx}.fieldName`}
                      type="hidden"
                      defaultValue={row.fieldName}
                    />
                    <FormInput
                      name={`mappings.${idx}.display`}
                      type="checkbox"
                      defaultValue={row.display}
                    />
                  </TableCell>
                  <TableCell>
                    <FormInput
                      name={`mappings.${idx}.edit`}
                      type="checkbox"
                      defaultValue={row.edit}
                    />
                  </TableCell>
                  <TableCell>
                    <FormInput
                      name={`mappings.${idx}.verify`}
                      type="checkbox"
                      defaultValue={row.verify}
                    />
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </form>
    </FormProvider>
  )
}
